import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { addDays, fmtDate, SHIFT_STYLE, todayWIB } from "@/components/staff/attendance/utils";

const mondayOf = (d) => {
  const day = new Date(`${d}T12:00:00Z`).getUTCDay();
  return addDays(d, -((day + 6) % 7));
};

const Cell = ({ row, date, config, onSet }) => {
  const v = row.days[date];
  return (
    <td className="p-1">
      <select value={v.source === "default" ? "" : v.shift} onChange={(e) => onSet(row.user_id, date, e.target.value)}
        className={`w-full min-w-[84px] text-xs font-semibold rounded-lg border px-1.5 py-1.5 ${SHIFT_STYLE[v.shift] || SHIFT_STYLE[""]}`}
        data-testid={`schedule-cell-${row.user_id}-${date}`}>
        <option value="">{v.source === "default" ? `Def: ${config.shifts.find((s) => s.id === row.default_shift)?.label || "–"}` : "Ikuti default"}</option>
        {config.shifts.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        <option value="libur">Libur</option>
      </select>
      {v.source === "tukar" && <div className="text-[10px] text-amber-700 font-semibold text-center mt-0.5">tukar shift</div>}
    </td>
  );
};

export const WeeklySchedule = ({ config, onChanged }) => {
  const [week, setWeek] = useState(mondayOf(todayWIB()));
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/schedule", { params: { week_start: week } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [week]);
  useEffect(() => { load(); }, [load]);

  const onSet = async (user_id, date, shift) => {
    try { await api.put("/admin/schedule", { entries: [{ user_id, date, shift }] }); load(); onChanged?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  const copyPrev = async () => {
    if (!window.confirm("Salin jadwal minggu lalu ke minggu ini? Jadwal yang sudah diisi di minggu ini akan ditimpa.")) return;
    try { const { data: r } = await api.post("/admin/schedule/copy-previous", null, { params: { week_start: week } }); toast.success(`${r.copied} jadwal disalin`); load(); onChanged?.(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="mt-8" data-testid="weekly-schedule">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="font-serif font-semibold text-xl">Jadwal shift mingguan</h3>
          <p className="text-sm text-muted2">Atur shift tiap karyawan per hari. Kosong = ikut shift default di akun staf.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setWeek(addDays(week, -7))} className="p-2 rounded-full bg-sage-100" aria-label="Minggu sebelumnya" data-testid="schedule-prev-week"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-semibold min-w-[150px] text-center" data-testid="schedule-week-label">{fmtDate(week)} – {fmtDate(addDays(week, 6))}</span>
          <button onClick={() => setWeek(addDays(week, 7))} className="p-2 rounded-full bg-sage-100" aria-label="Minggu berikutnya" data-testid="schedule-next-week"><ChevronRight className="w-4 h-4" /></button>
          <button onClick={copyPrev} className="px-3 py-2 rounded-full bg-sage-100 text-xs font-semibold flex items-center gap-1.5" data-testid="schedule-copy-prev"><Copy className="w-3.5 h-3.5" /> Salin minggu lalu</button>
        </div>
      </div>
      <div className="card-paper overflow-x-auto">
        <table className="w-full text-sm" data-testid="schedule-table">
          <thead>
            <tr className="text-left text-xs text-muted2 border-b border-sage-200">
              <th className="px-3 py-2.5 font-semibold sticky left-0 bg-paper">Karyawan</th>
              {data?.dates.map((d) => <th key={d} className={`px-1 py-2.5 font-semibold text-center ${d === todayWIB() ? "text-forest" : ""}`}>{fmtDate(d)}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-sage-200">
            {data?.staff.map((row) => (
              <tr key={row.user_id} data-testid={`schedule-row-${row.user_id}`}>
                <td className="px-3 py-2 sticky left-0 bg-paper min-w-[130px]"><div className="font-semibold">{row.name}</div><div className="text-xs text-muted2">{row.division || "-"}</div></td>
                {data.dates.map((d) => <Cell key={d} row={row} date={d} config={config} onSet={onSet} />)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
